"use client"

import { useState, useMemo } from "react"
import { RotateCcw, Target, AlertTriangle, Info } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

const T = {
  es: {
    datosNegocio: "Datos del negocio",
    limpiar: "Limpiar",
    costesFijos: "Costes fijos mensuales (€)",
    ayudaCostesFijos: "Alquiler, salarios, seguros, suministros fijos, etc.",
    ticketMedio: "Ticket medio (€)",
    ayudaTicket: "Gasto medio por comensal",
    costeVariable: "Coste variable por cubierto (€)",
    ayudaCosteVariable: "Food cost medio por comensal (materia prima)",
    queEs: "¿Qué es el punto de equilibrio?",
    explicacion:
      "Es el número mínimo de cubiertos que necesitas servir al mes para cubrir todos tus costes fijos. Por debajo de ese número, operas con pérdidas.",
    resultados: "Resultados",
    ticketBajo: "El ticket medio debe ser mayor que el coste variable por cubierto.",
    sinDatos: "Introduce los datos para ver el punto de equilibrio.",
    puntoEquilibrio: "Punto de equilibrio",
    cubiertosMes: "cubiertos al mes",
    necesitas: "Necesitas servir",
    cubiertosDia: (n: number) => `${n} cubiertos al día`,
    paraCubrir: "para cubrir gastos",
    ingresosObjetivo: "Ingresos objetivo",
    alMes: "al mes",
    margenContribucion: "Margen contribución",
    porCubierto: "por cubierto",
    desglose: "Desglose por cubierto",
    costeVariableCorto: "Coste variable",
    margen: "Margen",
  },
  en: {
    datosNegocio: "Business details",
    limpiar: "Reset",
    costesFijos: "Monthly fixed costs (€)",
    ayudaCostesFijos: "Rent, wages, insurance, fixed utilities, etc.",
    ticketMedio: "Average spend per cover (€)",
    ayudaTicket: "What each guest spends on average",
    costeVariable: "Variable cost per cover (€)",
    ayudaCosteVariable: "Average food cost per guest (ingredients)",
    queEs: "What is the break-even point?",
    explicacion:
      "It’s the minimum number of covers you need to serve each month to cover all your fixed costs. Below that number, you’re running at a loss.",
    resultados: "Results",
    ticketBajo: "Average spend must be higher than the variable cost per cover.",
    sinDatos: "Enter your figures to see your break-even point.",
    puntoEquilibrio: "Break-even point",
    cubiertosMes: "covers a month",
    necesitas: "You need to serve",
    cubiertosDia: (n: number) => `${n} covers a day`,
    paraCubrir: "to cover your costs",
    ingresosObjetivo: "Target revenue",
    alMes: "a month",
    margenContribucion: "Contribution margin",
    porCubierto: "per cover",
    desglose: "Breakdown per cover",
    costeVariableCorto: "Variable cost",
    margen: "Margin",
  },
}

const defaultValues = {
  costesFijos: 15000,
  ticketMedio: 25,
  costeVariable: 9,
}

export default function PuntoEquilibrioCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const eurTal = (n: number) => (idioma === "en" ? `€${n}` : `${n}€`)
  const [costesFijos, setCostesFijos] = useState(defaultValues.costesFijos)
  const [ticketMedio, setTicketMedio] = useState(defaultValues.ticketMedio)
  const [costeVariable, setCosteVariable] = useState(defaultValues.costeVariable)

  const results = useMemo(() => {
    const margenContribucion = ticketMedio - costeVariable
    const peCubiertos =
      margenContribucion > 0 ? costesFijos / margenContribucion : 0
    const peEuros = peCubiertos * ticketMedio
    const cubiertoDiario = peCubiertos > 0 ? peCubiertos / 30 : 0

    return {
      margenContribucion,
      peCubiertos: Math.ceil(peCubiertos),
      peEuros,
      cubiertoDiario: Math.ceil(cubiertoDiario),
    }
  }, [costesFijos, ticketMedio, costeVariable])

  const reset = () => {
    setCostesFijos(defaultValues.costesFijos)
    setTicketMedio(defaultValues.ticketMedio)
    setCosteVariable(defaultValues.costeVariable)
  }

  const isValid = ticketMedio > costeVariable && costesFijos > 0

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosNegocio}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.costesFijos}
            </label>
            <input
              type="number"
              value={costesFijos || ""}
              onChange={(e) => setCostesFijos(parseFloat(e.target.value) || 0)}
              placeholder="15000"
              min="0"
              step="any"
              className={inputClass}
            />
            <p className="text-xs text-zinc-400 mt-1">
              {t.ayudaCostesFijos}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.ticketMedio}
            </label>
            <input
              type="number"
              value={ticketMedio || ""}
              onChange={(e) => setTicketMedio(parseFloat(e.target.value) || 0)}
              placeholder="25"
              min="0"
              step="any"
              className={inputClass}
            />
            <p className="text-xs text-zinc-400 mt-1">
              {t.ayudaTicket}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.costeVariable}
            </label>
            <input
              type="number"
              value={costeVariable || ""}
              onChange={(e) => setCosteVariable(parseFloat(e.target.value) || 0)}
              placeholder="9"
              min="0"
              step="any"
              className={inputClass}
            />
            <p className="text-xs text-zinc-400 mt-1">
              {t.ayudaCosteVariable}
            </p>
          </div>

          {/* Info box */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-sky-50 border border-sky-200">
            <Info className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-700">
              <p className="font-medium text-zinc-900 mb-1">{t.queEs}</p>
              <p>{t.explicacion}</p>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {!isValid && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-700">
                {ticketMedio <= costeVariable
                  ? t.ticketBajo
                  : t.sinDatos}
              </p>
            </div>
          )}

          {isValid && (
            <>
              {/* Main KPI */}
              <div className="rounded-2xl p-6 text-center bg-sky-50">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-100 mb-3">
                  <Target className="w-6 h-6 text-sky-600" />
                </div>
                <p className="text-sm font-medium text-zinc-600 mb-1">
                  {t.puntoEquilibrio}
                </p>
                <p className="text-5xl font-bold text-sky-600">
                  {idioma === "en" ? numeroEn(results.peCubiertos, 0) : results.peCubiertos.toLocaleString("es-ES")}
                </p>
                <p className="text-sm text-zinc-500 mt-1">{t.cubiertosMes}</p>
              </div>

              {/* Daily target highlight */}
              <div className="rounded-2xl p-4 bg-white border-2 border-sky-200 text-center">
                <p className="text-lg font-semibold text-zinc-900">
                  {t.necesitas}{" "}
                  <span className="text-sky-600">{t.cubiertosDia(results.cubiertoDiario)}</span>{" "}
                  {t.paraCubrir}
                </p>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-zinc-200 p-4">
                  <p className="text-xs text-zinc-500 mb-1">{t.ingresosObjetivo}</p>
                  <p className="text-xl font-bold text-zinc-900">
                    {idioma === "en" ? eurosEn(results.peEuros, 0) : `${results.peEuros.toLocaleString("es-ES", { maximumFractionDigits: 0 })}€`}
                  </p>
                  <p className="text-xs text-zinc-400">{t.alMes}</p>
                </div>
                <div className="bg-white rounded-xl border border-zinc-200 p-4">
                  <p className="text-xs text-zinc-500 mb-1">{t.margenContribucion}</p>
                  <p className="text-xl font-bold text-zinc-900">
                    {eur(results.margenContribucion)}
                  </p>
                  <p className="text-xs text-zinc-400">{t.porCubierto}</p>
                </div>
              </div>

              {/* Visual breakdown */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-zinc-700">{t.desglose}</p>
                <div className="flex rounded-full h-5 overflow-hidden">
                  {ticketMedio > 0 && (
                    <>
                      <div
                        className="bg-red-400 transition-all duration-300"
                        style={{
                          width: `${(costeVariable / ticketMedio) * 100}%`,
                        }}
                        title={`${t.costeVariableCorto}: ${eurTal(costeVariable)}`}
                      />
                      <div
                        className="bg-sky-400 transition-all duration-300"
                        style={{
                          width: `${(results.margenContribucion / ticketMedio) * 100}%`,
                        }}
                        title={`${t.margenContribucion}: ${eur(results.margenContribucion)}`}
                      />
                    </>
                  )}
                </div>
                <div className="flex gap-4 text-xs text-zinc-600">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    {t.costeVariableCorto} ({eurTal(costeVariable)})
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-sky-400" />
                    {t.margen} ({eur(results.margenContribucion)})
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
